/**
 * Admin auth — soporta dos modos:
 *
 * 1. **Superadmin** — login con `ADMIN_BOOTSTRAP_TOKEN`. Acceso total al panel
 *    de plataforma (`/`, `/tenants`, etc). Identificado por `mode='superadmin'`.
 *
 * 2. **Tenant owner** — login con email+password (Better-Auth session cookie).
 *    Tras login, leemos `/api/admin/me` para conocer las memberships y
 *    redirigimos al primer tenant. Identificado por `mode='owner'`.
 *
 * En modo owner, las requests al backend usan la cookie de session que
 * Better-Auth setea en `/api/auth/sign-in/email` — por eso `fetch` necesita
 * `credentials: 'include'`.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AuthMode = 'superadmin' | 'owner'

export interface OwnerTenantMembership {
  tenantId: string
  role: string
  tenantName: string
  tenantSlug: string
  tenantStatus: string
}

interface AuthState {
  apiUrl: string
  /** Superadmin bootstrap token (legacy mode). Null en modo owner. */
  adminToken: string | null
  /** Modo de auth activo. null = no logueado. */
  mode: AuthMode | null
  /** Email del usuario logueado (cualquier modo). */
  userEmail: string | null
  /** Memberships del owner — vacío en modo superadmin. */
  ownerMemberships: OwnerTenantMembership[]
  activeTenantId: string | null
  activeTenantApiKey: string | null
  setApiUrl: (url: string) => void
  setAdminToken: (token: string | null) => void
  setOwnerSession: (email: string, memberships: OwnerTenantMembership[]) => void
  setActiveTenant: (tenantId: string | null, apiKey: string | null) => void
  logout: () => void
  isAuthenticated: () => boolean
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      apiUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:3000',
      adminToken: null,
      mode: null,
      userEmail: null,
      ownerMemberships: [],
      activeTenantId: null,
      activeTenantApiKey: null,
      setApiUrl: (url) => set({ apiUrl: url.replace(/\/$/, '') }),
      setAdminToken: (token) =>
        set({
          adminToken: token,
          mode: token ? 'superadmin' : null,
          userEmail: token ? 'system@core' : null,
          ownerMemberships: [],
        }),
      setOwnerSession: (email, memberships) =>
        set({
          adminToken: null,
          mode: 'owner',
          userEmail: email,
          ownerMemberships: memberships,
        }),
      setActiveTenant: (tenantId, apiKey) =>
        set({ activeTenantId: tenantId, activeTenantApiKey: apiKey }),
      logout: () =>
        set({
          adminToken: null,
          mode: null,
          userEmail: null,
          ownerMemberships: [],
          activeTenantId: null,
          activeTenantApiKey: null,
        }),
      isAuthenticated: () => get().mode !== null,
    }),
    { name: 'core-admin-auth' },
  ),
)

export function authHeaders(): HeadersInit {
  const { adminToken } = useAuth.getState()
  // En modo owner no hay header — la session viaja en la cookie cross-origin
  // (fetch con credentials: 'include').
  return adminToken ? { Authorization: `Bearer ${adminToken}` } : {}
}

/**
 * Headers para llamadas /api/v1/* (tenant-scoped).
 *
 * Dos paths:
 * 1. Si hay activeTenantApiKey (estilo storefront) → la usa directo
 * 2. Si no, pero hay adminToken + activeTenantId → usa admin impersonation
 *    (Authorization: Bearer <adminToken> + X-Core-Store-Id: <tenantId>)
 *    El backend valida que el adminToken sea válido antes de aceptar el header.
 */
export function tenantHeaders(): HeadersInit {
  const { activeTenantApiKey, adminToken, activeTenantId } = useAuth.getState()
  if (activeTenantApiKey) return { Authorization: `Bearer ${activeTenantApiKey}` }
  if (adminToken && activeTenantId) {
    return {
      Authorization: `Bearer ${adminToken}`,
      'X-Core-Store-Id': activeTenantId,
    }
  }
  return {}
}
